import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf, RendererOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {IconButton} from '../../../libs/shared/ui/src/components/buttons/iconButton';
import {PopoverCoordinator} from '../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {ellipsis} from '../../../libs/shared/ui/src/styles/mixins';
import {DataFieldsTableColumnModel} from '../../models/api/dataFieldsTableModel';
import {DataFieldsTableColumnDialog} from './dataFieldsTableColumnDialog';

/*
 * Props.
 */

interface DataFieldsTableHeaderProps {
  column: DataFieldsTableColumnModel;
  onEditColumn: HandlerOf<Partial<DataFieldsTableColumnModel>>;
  onDeleteColumn: Handler;
  columnValidator: RendererOf<Partial<DataFieldsTableColumnModel>>;
}

/*
 * Styles.
 */

const StyledWrapper = styled.div`
  padding: 12px 5px 12px 12px;
  flex: 1;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr 30px;
  align-items: center;

  > :last-child[aria-expanded='false'] {
    visibility: hidden;
  }

  &:hover > :last-child {
    visibility: visible;
  }
`;

const StyledColumnNameWrapper = styled.div`
  ${ellipsis()}
  width: 1;
`;

const StyledIconButton = styled(IconButton)`
  min-width: fit-content;
`;

/*
 * Component.
 */

export const DataFieldsTableHeader: FC<DataFieldsTableHeaderProps> = ({
  column,
  onEditColumn,
  onDeleteColumn,
  columnValidator,
}) => (
  <StyledWrapper>
    <StyledColumnNameWrapper>{column.name}</StyledColumnNameWrapper>
    <PopoverCoordinator
      renderButton={(isOpen) => (
        <StyledIconButton
          isOpen={isOpen}
          hasHover={isOpen}
          iconName="moreVertical"
          label={<Trans id="rule-editor-data-table-header-more">Edit column</Trans>}
        />
      )}
      renderModal={(popoverProps) => (
        <DataFieldsTableColumnDialog
          {...popoverProps}
          mode="edit"
          columnValidator={columnValidator}
          initialData={column}
          onSubmit={onEditColumn}
          onDelete={onDeleteColumn}
        />
      )}
    />
  </StyledWrapper>
);
